export const TICKET_STATE = {
  APERTO: "aperto",
  IN_LAVORAZIONE: "in-lavorazione",
  CHIUSO: "chiuso",
};

export const STATE_FILTER_OPTIONS = [
  { value: null, label: "Tutti" },
  { value: TICKET_STATE.APERTO, label: "Aperto" },
  { value: TICKET_STATE.IN_LAVORAZIONE, label: "In lavorazione" },
  { value: TICKET_STATE.CHIUSO, label: "Chiuso" },
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

export function normalizeTimestamp(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && raw.length >= 3) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = raw;
    return new Date(y, mo - 1, d, h, mi, s).toISOString();
  }
  return null;
}

export function formatTicketDate(raw) {
  const normalized = normalizeTimestamp(raw);
  if (!normalized) return "";
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad2(dt.getHours())}:${pad2(dt.getMinutes())}`;
}

export function getTicketStateLabel(state) {
  switch (state) {
    case TICKET_STATE.APERTO:
      return "Aperto";
    case TICKET_STATE.IN_LAVORAZIONE:
      return "In lavorazione";
    case TICKET_STATE.CHIUSO:
      return "Chiuso";
    default:
      return state ?? "";
  }
}

export function getTicketStateColor(state) {
  switch (state) {
    case TICKET_STATE.APERTO:
      return "#b30047";
    case TICKET_STATE.IN_LAVORAZIONE:
      return "#0b3d87";
    case TICKET_STATE.CHIUSO:
      return "#006767";
    default:
      return "#757575";
  }
}

export function isAssignee(ticket, loginId) {
  if (!ticket || loginId == null) return false;
  return Number(ticket.operatorId) === Number(loginId);
}

export function getStateFilterOptions() {
  return STATE_FILTER_OPTIONS;
}

export function canCloseTicket(ticket, loginId, isAdmin) {
  if (ticket?.state !== TICKET_STATE.IN_LAVORAZIONE) return false;
  if (isAdmin) return true;
  return isAssignee(ticket, loginId);
}

export function canReopenTicket(ticket, loginId, isAdmin) {
  if (ticket?.state !== TICKET_STATE.CHIUSO) {
    return false;
  }
  if (isAdmin) {
    return true;
  }
  return isAssignee(ticket, loginId);
}

function toMillis(raw) {
  const normalized = normalizeTimestamp(raw);
  return normalized ? new Date(normalized).getTime() : 0;
}

export function sortChatMessagesAscending(items) {
  return [...items].sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));
}

export function mergeChatMessages(existing, incoming) {
  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    const prev = byId.get(item.id);
    byId.set(item.id, prev ? { ...prev, ...item } : item);
  }
  return sortChatMessagesAscending([...byId.values()]);
}

export function normalizePollAfterTimestamp(raw) {
  const normalized = normalizeTimestamp(raw);
  if (!normalized) return undefined;
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return undefined;
  const pad = (value) => String(value).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

export function getPollAfterTimestamp(messages) {
  return normalizePollAfterTimestamp(getLatestChatTimestamp(messages));
}

export function collectOwnUnreadMessageIds(messages, isOwnMessage) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  return messages
    .filter((msg) => !msg.isDescription && isOwnMessage(msg) && !msg.read)
    .map((msg) => msg.id);
}

export function applyReadMessageIds(messages, readMessageIds) {
  if (!readMessageIds?.length) return messages;
  const ids = new Set(readMessageIds);
  let changed = false;
  const next = messages.map((msg) => {
    if (!ids.has(msg.id) || msg.read) return msg;
    changed = true;
    return { ...msg, read: true };
  });
  return changed ? next : messages;
}

export function applyPollResult(messages, { items = [], readMessageIds = [] } = {}) {
  let next = messages;
  if (items.length) {
    next = mergeChatMessages(next, items);
  }
  return applyReadMessageIds(next, readMessageIds);
}

export function mergeTicketSnapshots(existingTickets, snapshots) {
  if (!snapshots?.length || !existingTickets?.length) return existingTickets;
  const byId = new Map(snapshots.map((snapshot) => [snapshot.id, snapshot]));
  return existingTickets.map((ticket) => {
    const snapshot = byId.get(ticket.id);
    if (!snapshot) return ticket;
    return {
      ...ticket,
      state: snapshot.state ?? ticket.state,
      updatedAt: snapshot.updatedAt ?? ticket.updatedAt,
      unreadCount: snapshot.unreadCount == null ? ticket.unreadCount : Number(snapshot.unreadCount),
    };
  });
}

export function getLatestChatTimestamp(messages) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return null;
  }
  let latest = null;
  let latestMs = 0;
  for (const msg of messages) {
    const ms = toMillis(msg.timestamp);
    if (ms >= latestMs) {
      latestMs = ms;
      latest = msg.timestamp;
    }
  }
  return latest;
}

export function buildSentChatMessage({ messageId, message, attachmentName, isOperatorSide }) {
  return {
    id: messageId,
    operator: isOperatorSide,
    message: message?.trim() || null,
    attachmentName: attachmentName ?? null,
    timestamp: new Date().toISOString(),
    editable: true,
    read: false,
  };
}

export function canSendChatMessage(ticket, loginId) {
  return ticket?.state === TICKET_STATE.IN_LAVORAZIONE && isAssignee(ticket, loginId);
}

export function getChatDisabledTooltip(ticket, loginId) {
  if (ticket?.state === TICKET_STATE.APERTO) {
    return "Il ticket non è ancora in lavorazione, assegnalo ad un operatore o a te stesso.";
  }
  if (ticket?.state === TICKET_STATE.CHIUSO) {
    return "Il ticket è chiuso: la chat non è disponibile.";
  }
  if (ticket?.state === TICKET_STATE.IN_LAVORAZIONE && !isAssignee(ticket, loginId)) {
    return "Solo l'operatore assegnato può rispondere in chat.";
  }
  return "La chat non è disponibile.";
}

export function buildDescriptionMessage(ticket) {
  const description = ticket?.description?.trim();
  if (!description) return null;
  return {
    id: `desc-${ticket.id}`,
    operator: false,
    message: description,
    attachmentName: null,
    timestamp: ticket.createdAt,
    editable: false,
    isDescription: true,
    read: true,
  };
}

function toNumberOrNull(value) {
  return value == null ? null : Number(value);
}

function toNumberOrZero(value) {
  return value == null ? 0 : Number(value);
}

export function mapTicketItem(raw) {
  return {
    id: raw.id,
    subject: raw.subject,
    state: raw.state,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    unreadCount: toNumberOrZero(raw.unreadCount),
    vendorId: toNumberOrNull(raw.vendorId),
    vendorBusinessName: raw.vendorBusinessName ?? "",
    operatorId: toNumberOrNull(raw.operatorId),
    assigneeUserName: raw.assigneeUserName ?? "",
    description: raw.description ?? "",
  };
}
 